import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  FormControl,
  FormLabel,
  useColorModeValue
} from '@chakra-ui/react';
import {
  FiUsers,
  FiCheck,
  FiEye,
  FiAlertTriangle,
  FiRefreshCw,
  FiUserPlus
} from 'react-icons/fi';
import paymentSyncService from '../../../../../services/paymentSync';

const SyncComparator = ({ napsisData, customers, onComparisonComplete, onError }) => {
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentProcessing, setCurrentProcessing] = useState('');
  const [selectedLimit, setSelectedLimit] = useState(10); // Opción por defecto
  const [cancelRequested, setCancelRequested] = useState(false);
  const [createMissingUsers, setCreateMissingUsers] = useState(false); // Switch para crear usuarios
  const [isCreatingUsers, setIsCreatingUsers] = useState(false);

  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgColor = useColorModeValue("white", "gray.700");

  // Función helper para normalizar RUT (quitar puntos y guiones)
  const normalizeRut = (rut) => {
    if (!rut) return '';
    return rut.replace(/[.-]/g, ''); // Quitar puntos y guiones
  };

  // Función helper para normalizar mes
  const normalizeMes = (mes) => {
    const mesMap = {
      'enero': 1, 'febrero': 2, 'marzo': 3, 'abril': 4,
      'mayo': 5, 'junio': 6, 'julio': 7, 'agosto': 8,
      'septiembre': 9, 'octubre': 10, 'noviembre': 11, 'diciembre': 12
    };
    
    const mesNormalizado = mes ? mes.toLowerCase().trim() : '';
    const numeroMes = mesMap[mesNormalizado] || 1;
    
    return {
      numero: numeroMes,
      nombre: mesNormalizado,
      yearMonth: (year) => {
        const mesStr = numeroMes.toString().padStart(2, '0');
        return `${year}-${mesStr}`;
      }
    };
  };

  // Función para crear usuarios faltantes (solo para los usuarios que se van a procesar)
  const createMissingUsersFromNapsis = async (napsisDataLimited, existingCustomers) => {
    setIsCreatingUsers(true);
    setCurrentProcessing('Creando usuarios faltantes...');
    
    const usersToCreate = [];
    // Crear set de RUTs existentes normalizados
    const existingRuts = new Set(existingCustomers.map(c => {
      if (!c.username) return '';
      return normalizeRut(c.username);
    }));
    
    // Identificar usuarios que no existen (SOLO en el subconjunto limitado)
    napsisDataLimited.forEach(student => {
      // Filtrar header del Excel
      if (student.rut === 'Run Alumno' || student.nombre === 'Nombre Alumno') {
        return; // Saltar header
      }
      
      const cleanRut = student.rut.replace(/\./g, ''); // Mantener formato para username
      // Normalizar RUT para comparación
      const normalizedRut = normalizeRut(student.rut);
      if (!existingRuts.has(normalizedRut)) {
        // LOG ESPECÍFICO para ver qué RUTs no encuentra
        console.log(`📋 Usuario NAPSIS no encontrado en sistema: ${student.rut} → Compare: ${normalizedRut} | Nombre: ${student.nombre}`);
        usersToCreate.push({
          rut: student.rut,
          cleanRut: cleanRut,
          nombre: student.nombre
        });
      }
    });
    
    
    if (usersToCreate.length === 0) {
      setCurrentProcessing('No hay usuarios faltantes para crear');
      setIsCreatingUsers(false);
      return { created: 0, errors: 0 };
    }
    
    let created = 0;
    let errors = 0;
    
    for (const user of usersToCreate) {
      try {
        // Extraer nombre y apellido
        const nameParts = user.nombre.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Username = RUT completo (con guión, sin puntos)  
        const username = user.cleanRut;
        
        // Password = RUT sin dígito verificador
        const rutSinDV = user.cleanRut.split('-')[0];
        
        // Crear usuario via API WordPress
        const response = await fetch('/wp-json/wp/v2/users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}` // Si usas auth
          },
          body: JSON.stringify({
            username: username,
            email: `${username}@blaspascal.cl`, // Email consistente con el sistema
            password: rutSinDV,
            first_name: firstName,
            last_name: lastName,
            meta: {
              rut: user.rut
            }
          })
        });
        
        if (response.ok) {
          created++;
          // LOG ESPECÍFICO para RUTs con -k
          if (username.includes('-k')) {
            console.log(`✅ RUT con -k creado exitosamente: ${username}`);
          }
        } else {
          errors++;
          // LOG ESPECÍFICO para errores con RUTs -k
          if (username.includes('-k')) {
            console.log(`❌ ERROR creando RUT con -k: ${username}`, await response.text());
          }
        }
        
        setCurrentProcessing(`Creando usuarios: ${created + errors}/${usersToCreate.length}`);
        
      } catch (error) {
        errors++;
      }
    }
    
    setIsCreatingUsers(false);
    return { created, errors, total: usersToCreate.length };
  };

  const startComparison = async (limit) => {
    try {
      setIsComparing(true);
      setProgress(0);
      setCancelRequested(false);
      
      // LOG PRIMER PASO: Ver RUTs del Excel procesado
      console.log(`📊 EXCEL PROCESADO - Primeros 10 RUTs:`, napsisData.slice(0, 10).map(s => `${s.rut} - ${s.nombre}`));
      
      // PASO 1: Limitar datos NAPSIS según el límite seleccionado
      const napsisDataLimited = limit === -1 ? napsisData : napsisData.slice(0, limit);
      
      // PASO 2: Crear usuarios faltantes si está habilitado (solo para los limitados)
      if (createMissingUsers) {
        setCurrentProcessing('Creando usuarios faltantes antes de sincronizar...');
        const userCreationResult = await createMissingUsersFromNapsis(napsisDataLimited, customers);
        
        if (userCreationResult.created > 0) {
          // Recargar customers después de crear usuarios
          setCurrentProcessing('Recargando lista de usuarios...');
          // TODO: Aquí deberías recargar la lista de customers
          // Podría ser necesario un callback al componente padre
        }
        
      }
      
      setCurrentProcessing(`Iniciando comparación (límite: ${limit === -1 ? 'TODOS' : limit})...`);

      const maxProcessed = limit; // Usar límite seleccionado
      const maxSyncable = limit;  // Usar límite seleccionado

      const result = await paymentSyncService.comparePaymentStatus(
        napsisDataLimited,
        customers,
        (progressData) => {
          if (cancelRequested) {
            throw new Error('Operación cancelada por el usuario');
          }
          setProgress((progressData.processed / progressData.total) * 100);
          setCurrentProcessing(`Procesando: ${progressData.user} (${progressData.processed}/${progressData.total})`);
        },
        {
          maxProcessed: maxProcessed,
          maxSyncable: maxSyncable,
          createFines: true
        }
      );

      if (!cancelRequested) {
        setComparisonResult(result);
      }

    } catch (error) {
      console.error('Error en comparación:', error);
      if (error.message.includes('cancelada')) {
        setCurrentProcessing('Operación cancelada');
      } else if (onError) {
        onError(error);
      }
    } finally {
      setIsComparing(false);
      if (!cancelRequested) {
        setProgress(100);
        setCurrentProcessing('Comparación completada');
      }
    }
  };
  
  const cancelComparison = () => {
    setCancelRequested(true);
    setIsComparing(false);
    setProgress(0);
    setCurrentProcessing('Cancelando...');
    setTimeout(() => {
      setCurrentProcessing('');
      setComparisonResult(null);
    }, 1000);
  };

  if (isComparing || isCreatingUsers) {
    return (
      <Box bg={bgColor} border="1px" borderColor={borderColor} borderRadius="md" p={4}>
        <VStack spacing={4} align="stretch">
          <HStack spacing={3} align="center">
            <Spinner size="md" color={isCreatingUsers ? "orange.500" : "blue.500"} />
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="semibold">
                {isCreatingUsers ? "🆕 Creando Usuarios NAPSIS" : "Comparando datos NAPSIS vs Sistema"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {currentProcessing}
              </Text>
            </VStack>
          </HStack>
          
          <Progress value={progress} colorScheme={isCreatingUsers ? "orange" : "blue"} size="lg" hasStripe isAnimated />
          
          <HStack justify="space-between" fontSize="sm" color="gray.600">
            <Text>Progreso: {Math.round(progress)}%</Text>
            <Text>{napsisData.length} estudiantes NAPSIS vs {customers.length} usuarios del sistema</Text>
          </HStack>
          
          <HStack justify="center" mt={4}>
            <Button 
              colorScheme="red" 
              size="sm" 
              onClick={cancelComparison}
              leftIcon={<Icon as={FiRefreshCw} />}
            >
              Cancelar
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  if (!comparisonResult && !isComparing) {
    return (
      <VStack spacing={4} align="stretch">
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            Selecciona cuántos usuarios procesar para la comparación:
          </AlertDescription>
        </Alert>
        
        <Box bg={bgColor} border="1px" borderColor={borderColor} borderRadius="md" p={4}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3} align="center">
              <Icon as={FiUsers} color="blue.500" boxSize={6} />
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="semibold">
                  Opciones de Procesamiento
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {napsisData.length} estudiantes NAPSIS vs {customers.length} usuarios del sistema
                </Text>
              </VStack>
            </HStack>
            
            {/* Switch para crear usuarios faltantes */}
            <Box bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" p={4}>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="create-users" mb="0" flex={1}>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="md" fontWeight="semibold" color="orange.800">
                      🆕 Crear usuarios faltantes
                    </Text>
                    <Text fontSize="sm" color="orange.600">
                      Crear automáticamente usuarios de NAPSIS que no existen en el sistema
                      <br />
                      <strong>Username:</strong> RUT sin puntos ni guión | <strong>Password:</strong> RUT sin dígito verificador
                    </Text>
                  </VStack>
                </FormLabel>
                <Switch
                  id="create-users"
                  colorScheme="orange"
                  size="lg"
                  isChecked={createMissingUsers}
                  onChange={(e) => setCreateMissingUsers(e.target.checked)}
                  isDisabled={isComparing || isCreatingUsers}
                />
              </FormControl>
            </Box>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Button
                colorScheme="green"
                size="lg"
                onClick={() => startComparison(3)}
                leftIcon={<Icon as={FiEye} />}
              >
                🚀 Prueba Rápida
                <VStack spacing={0} ml={2}>
                  <Text fontSize="xs">3 usuarios</Text>
                  <Text fontSize="xs" color="green.200">~10 segundos</Text>
                </VStack>
              </Button>
              
              <Button
                colorScheme="blue"
                size="lg"
                onClick={() => startComparison(10)}
                leftIcon={<Icon as={FiEye} />}
              >
                📋 Vista Previa
                <VStack spacing={0} ml={2}>
                  <Text fontSize="xs">10 usuarios</Text>
                  <Text fontSize="xs" color="blue.200">~30 segundos</Text>
                </VStack>
              </Button>
              
              <Button
                colorScheme="purple"
                size="lg"
                onClick={() => startComparison(-1)}
                leftIcon={<Icon as={FiUsers} />}
              >
                🔄 Sincronización Completa
                <VStack spacing={0} ml={2}>
                  <Text fontSize="xs">Todos los usuarios</Text>
                  <Text fontSize="xs" color="purple.200">~{Math.ceil(napsisData.length / 10)} minutos</Text>
                </VStack>
              </Button>
            </SimpleGrid>
            
            <Alert status="warning" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Recomendación:</strong> Empieza con "Prueba Rápida" para verificar que todo funciona correctamente.
              </AlertDescription>
            </Alert>
          </VStack>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Estadísticas Resumidas */}
      <Box bg={bgColor} border="1px" borderColor={borderColor} borderRadius="md" p={4}>
        <VStack spacing={4} align="stretch">
          <HStack spacing={3}>
            <Icon as={FiUsers} color="blue.500" boxSize={6} />
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">
                Comparación Completada
              </Text>
              <Text fontSize="sm" color="gray.500">
                Análisis de datos NAPSIS vs Sistema
              </Text>
            </VStack>
          </HStack>
          
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat>
              <StatLabel>👥 Usuarios Procesados</StatLabel>
              <StatNumber>{comparisonResult.statistics.totalMatched}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>⚠️ Con Diferencias</StatLabel>
              <StatNumber>{comparisonResult.statistics.totalDiscrepancies}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>❌ No Encontrados</StatLabel>
              <StatNumber>{comparisonResult.notFound.length}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>🔄 Requieren Sync</StatLabel>
              <StatNumber>{comparisonResult.syncNeeded.length}</StatNumber>
            </Stat>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Tabla de Comparación Mes por Mes - LIMPIA */}
      <Box bg={bgColor} border="1px" borderColor={borderColor} borderRadius="md" p={4}>
        <VStack spacing={4} align="stretch">
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between" align="center">
              <HStack spacing={3}>
                <Icon as={FiEye} color="green.500" boxSize={5} />
                <Text fontSize="lg" fontWeight="semibold">
                  Vista Previa: Comparación Mes por Mes
                </Text>
              </HStack>
              <Badge variant="outline">
                Mostrando {Math.min(10, comparisonResult.matched.length)} de {comparisonResult.matched.length}
              </Badge>
            </HStack>
            
            <Alert status="info" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Cómo leer la tabla:</strong> Cada fila representa un mes. 
                La columna central muestra el mes/año siendo comparado. 
                Las columnas izquierda (NAPSIS) y derecha (Sistema) muestran los datos correspondientes a ese mes específico.
              </AlertDescription>
            </Alert>
          </VStack>
          
          <Box maxH="600px" overflowY="auto">
          <VStack spacing={4} align="stretch">
            {comparisonResult.matched.slice(0, 10).map((comparison, index) => {
              // Crear mapa de pagos NAPSIS por mes para comparación directa
              const napsisPaymentsByMonth = {};
              comparison.napsisData.pagos.forEach(pago => {
                const mesData = normalizeMes(pago.mes);
                const currentYear = new Date().getFullYear();
                const monthKey = mesData.yearMonth(currentYear);
                napsisPaymentsByMonth[monthKey] = pago;
              });

              // Crear mapa de órdenes del sistema por mes
              const systemOrdersByMonth = {};
              comparison.systemOrders.forEach(order => {
                const orderMeta = order.meta_data || [];
                const orderMes = orderMeta.find(meta => meta.key === 'mes')?.value;
                if (orderMes) {
                  systemOrdersByMonth[orderMes] = order;
                }
              });

              // Obtener todos los meses únicos para comparar
              const allMonths = new Set([
                ...Object.keys(napsisPaymentsByMonth),
                ...Object.keys(systemOrdersByMonth)
              ]);

              return (
                <Box key={index} border="2px" borderColor={borderColor} borderRadius="md" p={4} mb={4}>
                  <VStack spacing={4} align="stretch">
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" fontSize="md">
                          {comparison.customer.first_name} {comparison.customer.last_name}
                        </Text>
                        <HStack spacing={2}>
                          <Badge variant="outline">RUT: {comparison.customer.username}</Badge>
                          <Badge variant="outline">ID: {comparison.customer.id}</Badge>
                        </HStack>
                      </VStack>
                      <VStack align="end" spacing={1}>
                        {comparison.hasDiscrepancies && (
                          <Badge colorScheme="orange" variant="solid">
                            {comparison.discrepancies.length} diferencia(s)
                          </Badge>
                        )}
                        {comparison.needsSync && (
                          <Badge colorScheme="blue" variant="solid">
                            {comparison.syncActions.length} acción(es)
                          </Badge>
                        )}
                      </VStack>
                    </HStack>
                    
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th w="30%">📄 NAPSIS</Th>
                          <Th w="15%" textAlign="center">⚠️ MULTAS A CREAR</Th>
                          <Th w="15%" textAlign="center">🔄 ACCIONES A REALIZAR</Th>
                          <Th w="10%" textAlign="center">Mes/Año</Th>
                          <Th w="30%">🏛️ Sistema</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[...allMonths].sort().map(month => {
                          const napsisPago = napsisPaymentsByMonth[month];
                          const systemOrder = systemOrdersByMonth[month];
                          
                          // Buscar acciones para este mes
                          const monthActions = comparison.syncActions.filter(action => {
                            // Comparación directa para acciones que tienen campo mes
                            if (action.mes && action.mes === month) return true;
                            
                            // Para multas, convertir el mes en español a formato año-mes
                            if (action.multaData && action.multaData.mes) {
                              const mesData = normalizeMes(action.multaData.mes);
                              const currentYear = new Date().getFullYear();
                              return mesData.yearMonth(currentYear) === month;
                            }
                            
                            return false;
                          });

                          const fineActions = monthActions.filter(a => a.action === 'create_fine');
                          const otherActions = monthActions.filter(a => a.action !== 'create_fine');

                          return (
                            <Tr key={month}>
                              {/* NAPSIS Data */}
                              <Td>
                                {napsisPago ? (
                                  <VStack align="start" spacing={1}>
                                    <HStack spacing={2}>
                                      <Badge colorScheme={napsisPago.estado === 'completed' ? 'green' : 'orange'}>
                                        {napsisPago.estado === 'completed' ? '✅ Pagado' : '⏳ Pendiente'}
                                      </Badge>
                                    </HStack>
                                    <Text fontSize="xs" fontWeight="medium">
                                      Cuota #{napsisPago.numeroCuota} - {napsisPago.mes} {new Date().getFullYear()}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                      {napsisPago.concepto}
                                    </Text>
                                    {napsisPago.boleta && (
                                      <>
                                        <Text fontSize="xs" color="blue.600">
                                          📄 Boleta: {napsisPago.boleta.numero}
                                        </Text>
                                        <Text fontSize="xs" color="blue.600">
                                          📅 Pagado: {napsisPago.boleta.fechaFormateada}
                                        </Text>
                                        <Text fontSize="xs" color="green.600">
                                          💰 ${parseInt(napsisPago.boleta.total || 0).toLocaleString()}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          "{napsisPago.estado}"
                                        </Text>
                                      </>
                                    )}
                                  </VStack>
                                ) : (
                                  <Text fontSize="xs" color="gray.400">-</Text>
                                )}
                              </Td>

                              {/* Multas a crear */}
                              <Td textAlign="center">
                                {fineActions.length > 0 ? (
                                  <VStack spacing={1}>
                                    {fineActions.map((action, idx) => (
                                      <VStack key={idx} spacing={1}>
                                        <Badge colorScheme="red" variant="solid">
                                          ⚠️ {action.multaData?.cantidadMultas} multa(s)
                                        </Badge>
                                        <Text fontSize="xs" color="gray.600">
                                          📅 Venc: {action.multaData?.fechaVencimiento}
                                        </Text>
                                        <Text fontSize="xs" color="blue.600">
                                          💳 Pago: {action.multaData?.fechaPago}
                                        </Text>
                                        <Text fontSize="xs" color="red.600">
                                          {action.multaData?.mesesAtraso} mes(es) atraso
                                        </Text>
                                      </VStack>
                                    ))}
                                  </VStack>
                                ) : (
                                  <Text fontSize="xs" color="gray.400">-</Text>
                                )}
                              </Td>

                              {/* Acciones a realizar */}
                              <Td textAlign="center">
                                {(otherActions.length > 0 || fineActions.length > 0) ? (
                                  <VStack spacing={1}>
                                    {otherActions.map((action, idx) => (
                                      <Badge key={idx} colorScheme="blue" variant="outline">
                                        {action.action === 'update_status' ? '🔄 Actualizar' : '➕ Crear'}
                                      </Badge>
                                    ))}
                                    {fineActions.map((action, idx) => (
                                      <Badge key={`fine-${idx}`} colorScheme="red" variant="outline">
                                        ⚠️ Crear Multa
                                      </Badge>
                                    ))}
                                  </VStack>
                                ) : (
                                  <Badge colorScheme="gray" variant="outline">
                                    ⏸️ Sin acciones
                                  </Badge>
                                )}
                              </Td>

                              {/* Mes/Año */}
                              <Td textAlign="center">
                                <VStack spacing={1}>
                                  <Text fontSize="sm" fontWeight="bold">
                                    {month}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {month.split('-')[1]}/{month.split('-')[0].slice(2)}
                                  </Text>
                                  <Badge size="sm" colorScheme={napsisPago && systemOrder ? 'green' : 'orange'}>
                                    {napsisPago && systemOrder ? 'Ambos' : napsisPago ? 'Solo NAPSIS' : 'Solo Sistema'}
                                  </Badge>
                                </VStack>
                              </Td>

                              {/* Sistema Data */}
                              <Td>
                                {systemOrder ? (
                                  <VStack align="start" spacing={1}>
                                    <HStack spacing={2}>
                                      <Badge colorScheme={systemOrder.status === 'completed' ? 'green' : 'orange'}>
                                        {systemOrder.status === 'completed' ? '✅ Completada' : '⏳ Pendiente'}
                                      </Badge>
                                    </HStack>
                                    <Text fontSize="xs" fontWeight="medium">
                                      #{systemOrder.id} - ${parseInt(systemOrder.total).toLocaleString()}
                                    </Text>
                                    <HStack spacing={1}>
                                      <Icon as={systemOrder.status === 'completed' ? FiCheck : FiAlertTriangle} 
                                            boxSize={3} 
                                            color={systemOrder.status === 'completed' ? 'green.500' : 'orange.500'} />
                                      <Text fontSize="xs" color="gray.600">
                                        {systemOrder.status === 'completed' ? '✓ Match' : '⚠ Desincronizado'}
                                      </Text>
                                    </HStack>
                                  </VStack>
                                ) : (
                                  <Text fontSize="xs" color="gray.400">-</Text>
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </VStack>
                </Box>
              );
            })}
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* Botón para continuar */}
      <Box bg={bgColor} border="1px" borderColor={borderColor} borderRadius="md" p={4}>
        <VStack spacing={3} align="stretch">
          <HStack spacing={3} justify="center">
            <Button
              variant="outline"
              leftIcon={<Icon as={FiRefreshCw} />}
              onClick={() => {
                setComparisonResult(null);
                setProgress(0);
                setCurrentProcessing('');
              }}
            >
              Nueva Comparación
            </Button>
            
            <Button
              colorScheme="blue"
              size="lg"
              leftIcon={<Icon as={FiCheck} />}
              onClick={() => {
                if (onComparisonComplete) {
                  onComparisonComplete(comparisonResult);
                }
              }}
              isDisabled={!comparisonResult}
            >
              Continuar con Sincronización
              {comparisonResult?.syncNeeded?.length > 0 && (
                <Badge ml={2} colorScheme="white" variant="solid">
                  {comparisonResult.syncNeeded.length}
                </Badge>
              )}
            </Button>
          </HStack>
          
          {comparisonResult?.syncNeeded?.length === 0 && (
            <Alert status="success" mt={4}>
              <AlertIcon />
              <AlertDescription>
                ✅ No se requiere sincronización. Todos los datos están correctos.
              </AlertDescription>
            </Alert>
          )}
        </VStack>
      </Box>
    </VStack>
  );
};

export default SyncComparator;